<template>
    <div class="inventory-container">
        <div class="categories-management-container">
            <!-- Header Section -->
            <div class="header-section">
                <div class="title-section">
                    <h1 class="page-title">Product Categories Management</h1>
                    <p class="page-subtitle">Manage categories, subcategories, and attributes</p>
                </div>
                
                <div class="action-buttons">
                    <b-button @click="add_new_category" variant="primary" class="action-btn add-btn">
                        <b-icon icon="plus-circle" class="mr-2"></b-icon>
                        Add Category
                    </b-button>
                    <b-button @click="fetch_init" variant="outline-secondary" class="action-btn refresh-btn">
                        <b-icon icon="arrow-clockwise" class="mr-2"></b-icon>
                        Refresh
                    </b-button>
                </div>
            </div>

            <!-- Tabs Navigation -->
            <b-tabs card v-model="activeTab" class="inventory-tabs">
                <!-- Categories Tab -->
                <b-tab title="Categories & Subcategories" active>
                    <!-- Info Alert -->
                    <b-alert variant="info" show class="info-alert compact-alert">
                        <b-icon icon="info-circle" class="mr-2"></b-icon>
                        <small><strong>Important:</strong> Add new categories or edit existing ones. Click SAVE to save changes.</small>
                    </b-alert>

                    <!-- Loading State -->
                    <div v-if="items_being_loaded" class="loading-container">
                        <loading :active="items_being_loaded" :can-cancel="false" :is-full-page="false"></loading>
                    </div>

                    <!-- Categories List -->
                    <div v-else class="categories-list">
                        <div v-for="(item, idx) in items" :key="idx" class="category-row">
                            <!-- Main Category Row -->
                            <div class="category-main-row">
                                <div class="category-fields">
                                    <div class="field-group category-id-group">
                                        <label class="field-label">ID</label>
                                        <b-input readonly :value="item.category_id || 'NEW'" size="sm" class="field-input"></b-input>
                                    </div>
                                    <div class="field-group">
                                        <label class="field-label">CODE</label>
                                        <b-input v-model="item.category_code" size="sm" placeholder="CAT001" class="field-input"></b-input>
                                    </div>
                                    <div class="field-group field-group-expanded">
                                        <label class="field-label">NAME</label>
                                        <b-input v-model="item.category_name" size="sm" placeholder="Category name" class="field-input"></b-input>
                                    </div>
                                    <div class="field-group field-group-expanded">
                                        <label class="field-label">DESCRIPTION</label>
                                        <b-input v-model="item.category_description" size="sm" placeholder="Description" class="field-input"></b-input>
                                    </div>
                                    <div class="field-group date-group">
                                        <label class="field-label">DATE ADDED</label>
                                        <b-input :value="formatDate(item.ts)" disabled size="sm" class="field-input"></b-input>
                                    </div>
                                    <div class="field-group action-group">
                                        <b-button @click="save_category(item)" variant="success" size="sm" class="save-cat-btn" :disabled="!item.category_name || !item.category_code">
                                            <b-icon icon="check" class="mr-1"></b-icon> Save
                                        </b-button>
                                        <b-button @click="remove_category(idx)" variant="outline-danger" size="sm" v-if="!item.category_id">
                                            <b-icon icon="x"></b-icon>
                                        </b-button>
                                    </div>
                                </div>
                                
                                <div class="category-actions-row">
                                    <b-button @click="toggle_category_section(item, 'subcategories')" variant="outline-info" size="sm" class="toggle-btn">
                                        <b-icon :icon="item.show_subcategories ? 'chevron-up' : 'chevron-down'" class="mr-1"></b-icon>
                                        Subcategories ({{ item.subcategories ? item.subcategories.length : 0 }})
                                    </b-button>
                                    <b-button @click="toggle_category_section(item, 'attributes')" variant="outline-success" size="sm" class="toggle-btn">
                                        <b-icon :icon="item.show_attributes ? 'chevron-up' : 'chevron-down'" class="mr-1"></b-icon>
                                        Attributes
                                    </b-button>
                                </div>
                            </div>

                            <!-- Subcategories Section -->
                            <b-collapse :visible="item.show_subcategories" class="subcategories-section">
                                <div class="subcategories-wrapper">
                                    <div class="subcategories-header">
                                        <h6 class="section-title">Subcategories</h6>
                                        <b-button @click="add_new_subcategory(item)" variant="outline-primary" size="sm" :disabled="!item.category_id">
                                            <b-icon icon="plus" class="mr-1"></b-icon> Add
                                        </b-button>
                                    </div>
                                    
                                    <div v-if="item.subcategories && item.subcategories.length > 0" class="subcategories-list">
                                        <div v-for="(subcategory, sidx) in item.subcategories" :key="sidx" class="subcategory-row">
                                            <div class="subcategory-fields">
                                                <div class="field-group small-group">
                                                    <label class="field-label-sm">#</label>
                                                    <span class="index-badge">{{ sidx + 1 }}</span>
                                                </div>
                                                <div class="field-group">
                                                    <label class="field-label-sm">CODE</label>
                                                    <b-input v-model="subcategory.subcategory_code" size="sm" placeholder="SUB001" class="field-input-sm"></b-input>
                                                </div>
                                                <div class="field-group field-group-expanded">
                                                    <label class="field-label-sm">NAME</label>
                                                    <b-input v-model="subcategory.subcategory_name" size="sm" placeholder="Subcategory name" class="field-input-sm"></b-input>
                                                </div>
                                                <div class="field-group field-group-expanded">
                                                    <label class="field-label-sm">DESCRIPTION</label>
                                                    <b-input v-model="subcategory.subcategory_description" size="sm" placeholder="Description" class="field-input-sm"></b-input>
                                                </div>
                                                <div class="field-group">
                                                    <label class="field-label-sm">PROPERTIES</label>
                                                    <b-input v-model="subcategory.properties_string" size="sm" placeholder='{"color": "red"}' class="field-input-sm"></b-input>
                                                </div>
                                                <div class="field-group action-group-sm">
                                                    <b-button @click="save_subcategory(item, subcategory)" variant="success" size="sm" class="action-icon-btn" :disabled="!subcategory.subcategory_name || !subcategory.subcategory_code">
                                                        <b-icon icon="check"></b-icon>
                                                    </b-button>
                                                    <b-button @click="delete_subcategory(item, subcategory, sidx)" variant="outline-danger" size="sm" class="action-icon-btn" v-if="subcategory.id">
                                                        <b-icon icon="trash"></b-icon>
                                                    </b-button>
                                                    <b-button @click="toggle_subsubcategories(item, subcategory)" variant="outline-secondary" size="sm" class="toggle-sub-btn" :disabled="!subcategory.id">
                                                        <b-icon :icon="subcategory.show_subsubcategories ? 'chevron-up' : 'chevron-down'"></b-icon>
                                                        Sub-Sub
                                                    </b-button>
                                                </div>
                                            </div>
                                            
                                            <!-- Sub-Subcategories Section -->
                                            <b-collapse :visible="subcategory.show_subsubcategories" class="subsubcategories-section">
                                                <div class="subsubcategories-wrapper">
                                                    <div class="subsubcategories-header">
                                                        <small class="text-muted">Sub-Subcategories</small>
                                                        <b-button @click="add_new_subsubcategory(subcategory)" variant="outline-primary" size="sm" :disabled="!subcategory.id">
                                                            <b-icon icon="plus" class="mr-1"></b-icon> Add
                                                        </b-button>
                                                    </div>
                                                    
                                                    <div v-if="subcategory.subsubcategories && subcategory.subsubcategories.length > 0" class="subsubcategories-list">
                                                        <div v-for="(subsubcategory, ssidx) in subcategory.subsubcategories" :key="ssidx" class="subsubcategory-row">
                                                            <div class="subsubcategory-fields">
                                                                <div class="field-group tiny-group">
                                                                    <span class="tiny-badge">{{ ssidx + 1 }}</span>
                                                                </div>
                                                                <div class="field-group">
                                                                    <b-input v-model="subsubcategory.subsubcategory_code" size="sm" placeholder="CODE" class="field-input-xs"></b-input>
                                                                </div>
                                                                <div class="field-group field-group-expanded">
                                                                    <b-input v-model="subsubcategory.subsubcategory_name" size="sm" placeholder="Name" class="field-input-xs"></b-input>
                                                                </div>
                                                                <div class="field-group field-group-expanded">
                                                                    <b-input v-model="subsubcategory.subsubcategory_description" size="sm" placeholder="Desc" class="field-input-xs"></b-input>
                                                                </div>
                                                                <div class="field-group">
                                                                    <b-input v-model="subsubcategory.properties_string" size="sm" placeholder="Properties" class="field-input-xs"></b-input>
                                                                </div>
                                                                <div class="field-group action-group-xs">
                                                                    <b-button @click="save_subsubcategory(subcategory, subsubcategory)" variant="success" size="sm" class="action-icon-btn-xs" :disabled="!subsubcategory.subsubcategory_name || !subsubcategory.subsubcategory_code">
                                                                        <b-icon icon="check"></b-icon>
                                                                    </b-button>
                                                                    <b-button @click="delete_subsubcategory(subcategory, subsubcategory, ssidx)" variant="outline-danger" size="sm" class="action-icon-btn-xs" v-if="subsubcategory.id">
                                                                        <b-icon icon="trash"></b-icon>
                                                                    </b-button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div v-else class="empty-subsub">
                                                        <small class="text-muted">No sub-subcategories</small>
                                                    </div>
                                                </div>
                                            </b-collapse>
                                        </div>
                                    </div>
                                    <div v-else class="empty-subcategories">
                                        <small class="text-muted">No subcategories</small>
                                    </div>
                                </div>
                            </b-collapse>

                            <!-- Attributes Section -->
                            <b-collapse :visible="item.show_attributes" class="attributes-section">
                                <div class="attributes-wrapper">
                                    <b-tabs small class="attributes-tabs">
                                        <b-tab title="Types">
                                            <div class="attribute-content">
                                                <div class="attribute-header">
                                                    <small>Select types</small>
                                                    <b-button @click="save_category_types(item)" variant="success" size="sm">Save</b-button>
                                                </div>
                                                <div class="checkbox-grid">
                                                    <b-form-checkbox v-for="(type, tidx) in item.types" :key="tidx" v-model="type.is_mapped" size="sm">
                                                        {{ type.type_name }} <span class="text-muted">({{ type.type_code }})</span>
                                                    </b-form-checkbox>
                                                </div>
                                            </div>
                                        </b-tab>
                                        <b-tab title="Colors">
                                            <div class="attribute-content">
                                                <div class="attribute-header">
                                                    <small>Select colors</small>
                                                    <b-button @click="save_category_colors(item)" variant="success" size="sm">Save</b-button>
                                                </div>
                                                <div class="checkbox-grid">
                                                    <b-form-checkbox v-for="(color, cidx) in item.colors" :key="cidx" v-model="color.is_mapped" size="sm">
                                                        <span class="color-dot" :style="{backgroundColor: color.hex_code || '#ccc'}"></span>
                                                        {{ color.color_name }} <span class="text-muted">({{ color.color_code }})</span>
                                                    </b-form-checkbox>
                                                </div>
                                            </div>
                                        </b-tab>
                                        <b-tab title="Sizes">
                                            <div class="attribute-content">
                                                <div class="attribute-header">
                                                    <small>Select sizes</small>
                                                    <b-button @click="save_category_sizes(item)" variant="success" size="sm">Save</b-button>
                                                </div>
                                                <div class="checkbox-grid">
                                                    <b-form-checkbox v-for="(size, sidx) in item.sizes" :key="sidx" v-model="size.is_mapped" size="sm">
                                                        {{ size.size_name }} <span class="text-muted">({{ size.size_code }})</span>
                                                    </b-form-checkbox>
                                                </div>
                                            </div>
                                        </b-tab>
                                        <b-tab title="Materials">
                                            <div class="attribute-content">
                                                <div class="attribute-header">
                                                    <small>Select materials</small>
                                                    <b-button @click="save_category_materials(item)" variant="success" size="sm">Save</b-button>
                                                </div>
                                                <div class="checkbox-grid">
                                                    <b-form-checkbox v-for="(material, midx) in item.materials" :key="midx" v-model="material.is_mapped" size="sm">
                                                        {{ material.material_name }} <span class="text-muted">({{ material.material_code }})</span>
                                                    </b-form-checkbox>
                                                </div>
                                            </div>
                                        </b-tab>
                                        <b-tab title="Functionalities">
                                            <div class="attribute-content">
                                                <div class="attribute-header">
                                                    <small>Select functionalities</small>
                                                    <b-button @click="save_category_functionalities(item)" variant="success" size="sm">Save</b-button>
                                                </div>
                                                <div class="checkbox-grid">
                                                    <b-form-checkbox v-for="(func, fidx) in item.functionalities" :key="fidx" v-model="func.is_mapped" size="sm">
                                                        {{ func.functionality_name }} <span class="text-muted">({{ func.functionality_code }})</span>
                                                    </b-form-checkbox>
                                                </div>
                                            </div>
                                        </b-tab>
                                        <b-tab title="Bed Type">
                                            <div class="attribute-content">
                                                <div class="attribute-header">
                                                    <small>Select bed types</small>
                                                    <b-button @click="save_category_bed_type_styles(item)" variant="success" size="sm">Save</b-button>
                                                </div>
                                                <div class="checkbox-grid">
                                                    <b-form-checkbox v-for="(style, bidx) in item.bed_type_styles" :key="bidx" v-model="style.is_mapped" size="sm">
                                                        {{ style.style_name }} <span class="text-muted">({{ style.style_code }})</span>
                                                    </b-form-checkbox>
                                                </div>
                                            </div>
                                        </b-tab>
                                        <b-tab title="Styles">
                                            <div class="attribute-content">
                                                <div class="attribute-header">
                                                    <small>Select styles</small>
                                                    <b-button @click="save_category_styles(item)" variant="success" size="sm">Save</b-button>
                                                </div>
                                                <div class="checkbox-grid">
                                                    <b-form-checkbox v-for="(style, sidx) in item.styles" :key="sidx" v-model="style.is_mapped" size="sm">
                                                        {{ style.style_name }} <span class="text-muted">({{ style.style_code }})</span>
                                                    </b-form-checkbox>
                                                </div>
                                            </div>
                                        </b-tab>
                                        <b-tab title="Shapes">
                                            <div class="attribute-content">
                                                <div class="attribute-header">
                                                    <small>Select sectional shapes</small>
                                                    <b-button @click="save_category_sectional_shapes(item)" variant="success" size="sm">Save</b-button>
                                                </div>
                                                <div class="checkbox-grid">
                                                    <b-form-checkbox v-for="(shape, shidx) in item.sectional_shapes" :key="shidx" v-model="shape.is_mapped" size="sm">
                                                        {{ shape.shape_name }} <span class="text-muted">({{ shape.shape_code }})</span>
                                                    </b-form-checkbox>
                                                </div>
                                            </div>
                                        </b-tab>
                                    </b-tabs>
                                </div>
                            </b-collapse>
                        </div>
                    </div>
                </b-tab>

                <!-- Global Attributes Tab -->
                <b-tab title="Global Attributes">
                    <div class="global-attributes-container">
                        <b-tabs small class="global-tabs">
                            <b-tab title="Types">
                                <attribute-management ref="typeManager" title="Product Types" :items="global_types" :fields="[
                                    {key: 'type_code', label: 'Code', sortable: true},
                                    {key: 'type_name', label: 'Name', sortable: true},
                                    {key: 'type_description', label: 'Description'},
                                    {key: 'actions', label: ''}
                                ]" item-key="id" :fetch-method="fetch_global_types" :save-method="save_type" @refresh="fetch_global_types">
                                    <template v-slot:form-fields="{ item }">
                                        <b-form-group label="Code" label-for="type-code">
                                            <b-form-input id="type-code" v-model="item.type_code" placeholder="Enter type code" required size="sm"></b-form-input>
                                        </b-form-group>
                                        <b-form-group label="Name" label-for="type-name">
                                            <b-form-input id="type-name" v-model="item.type_name" placeholder="Enter type name" required size="sm"></b-form-input>
                                        </b-form-group>
                                        <b-form-group label="Description" label-for="type-description">
                                            <b-form-textarea id="type-description" v-model="item.type_description" placeholder="Enter description" rows="2" size="sm"></b-form-textarea>
                                        </b-form-group>
                                    </template>
                                </attribute-management>
                            </b-tab>
                            <b-tab title="Colors">
                                <attribute-management ref="colorManager" title="Product Colors" :items="global_colors" :fields="[
                                    {key: 'color_code', label: 'Code', sortable: true},
                                    {key: 'color_name', label: 'Name', sortable: true},
                                    {key: 'hex_code', label: 'Hex'},
                                    {key: 'actions', label: ''}
                                ]" item-key="id" :fetch-method="fetch_global_colors" :save-method="save_color" @refresh="fetch_global_colors">
                                    <template v-slot:form-fields="{ item }">
                                        <b-form-group label="Code" label-for="color-code">
                                            <b-form-input id="color-code" v-model="item.color_code" placeholder="Enter color code" required size="sm"></b-form-input>
                                        </b-form-group>
                                        <b-form-group label="Name" label-for="color-name">
                                            <b-form-input id="color-name" v-model="item.color_name" placeholder="Enter color name" required size="sm"></b-form-input>
                                        </b-form-group>
                                        <b-form-group label="Hex Code" label-for="hex-code">
                                            <b-form-input id="hex-code" v-model="item.hex_code" placeholder="#FFFFFF" size="sm"></b-form-input>
                                        </b-form-group>
                                    </template>
                                </attribute-management>
                            </b-tab>
                            <b-tab title="Sizes">
                                <attribute-management ref="sizeManager" title="Product Sizes" :items="global_sizes" :fields="[
                                    {key: 'size_code', label: 'Code', sortable: true},
                                    {key: 'size_name', label: 'Name', sortable: true},
                                    {key: 'dimensions', label: 'Dimensions'},
                                    {key: 'actions', label: ''}
                                ]" item-key="id" :fetch-method="fetch_global_sizes" :save-method="save_size" @refresh="fetch_global_sizes">
                                    <template v-slot:form-fields="{ item }">
                                        <b-form-group label="Code" label-for="size-code">
                                            <b-form-input id="size-code" v-model="item.size_code" placeholder="Enter size code" required size="sm"></b-form-input>
                                        </b-form-group>
                                        <b-form-group label="Name" label-for="size-name">
                                            <b-form-input id="size-name" v-model="item.size_name" placeholder="Enter size name" required size="sm"></b-form-input>
                                        </b-form-group>
                                        <b-form-group label="Dimensions" label-for="dimensions">
                                            <b-form-input id="dimensions" v-model="item.dimensions" placeholder="e.g., 10x20x30 cm" size="sm"></b-form-input>
                                        </b-form-group>
                                    </template>
                                </attribute-management>
                            </b-tab>
                            <b-tab title="Materials">
                                <attribute-management ref="materialManager" title="Product Materials" :items="global_materials" :fields="[
                                    {key: 'material_code', label: 'Code', sortable: true},
                                    {key: 'material_name', label: 'Name', sortable: true},
                                    {key: 'description', label: 'Description'},
                                    {key: 'actions', label: ''}
                                ]" item-key="id" :fetch-method="fetch_global_materials" :save-method="save_material" @refresh="fetch_global_materials">
                                    <template v-slot:form-fields="{ item }">
                                        <b-form-group label="Code" label-for="material-code">
                                            <b-form-input id="material-code" v-model="item.material_code" placeholder="Enter material code" required size="sm"></b-form-input>
                                        </b-form-group>
                                        <b-form-group label="Name" label-for="material-name">
                                            <b-form-input id="material-name" v-model="item.material_name" placeholder="Enter material name" required size="sm"></b-form-input>
                                        </b-form-group>
                                        <b-form-group label="Description" label-for="material-description">
                                            <b-form-textarea id="material-description" v-model="item.description" placeholder="Enter description" rows="2" size="sm"></b-form-textarea>
                                        </b-form-group>
                                    </template>
                                </attribute-management>
                            </b-tab>
                            <b-tab title="Functionalities">
                                <attribute-management ref="functionalityManager" title="Product Functionalities" :items="global_functionalities" :fields="[
                                    {key: 'functionality_code', label: 'Code', sortable: true},
                                    {key: 'functionality_name', label: 'Name', sortable: true},
                                    {key: 'description', label: 'Description'},
                                    {key: 'actions', label: ''}
                                ]" item-key="id" :fetch-method="fetch_global_functionalities" :save-method="save_functionality" @refresh="fetch_global_functionalities">
                                    <template v-slot:form-fields="{ item }">
                                        <b-form-group label="Code" label-for="func-code">
                                            <b-form-input id="func-code" v-model="item.functionality_code" placeholder="Enter functionality code" required size="sm"></b-form-input>
                                        </b-form-group>
                                        <b-form-group label="Name" label-for="func-name">
                                            <b-form-input id="func-name" v-model="item.functionality_name" placeholder="Enter functionality name" required size="sm"></b-form-input>
                                        </b-form-group>
                                        <b-form-group label="Description" label-for="func-description">
                                            <b-form-textarea id="func-description" v-model="item.description" placeholder="Enter description" rows="2" size="sm"></b-form-textarea>
                                        </b-form-group>
                                    </template>
                                </attribute-management>
                            </b-tab>
                            <b-tab title="Bed Types">
                                <attribute-management ref="bedTypeStyleManager" title="Bed Types" :items="global_bed_type_styles" :fields="[
                                    {key: 'style_code', label: 'Code', sortable: true},
                                    {key: 'style_name', label: 'Name', sortable: true},
                                    {key: 'description', label: 'Description'},
                                    {key: 'actions', label: ''}
                                ]" item-key="id" :fetch-method="fetch_global_bed_type_styles" :save-method="save_bed_type_style" @refresh="fetch_global_bed_type_styles">
                                    <template v-slot:form-fields="{ item }">
                                        <b-form-group label="Code" label-for="style-code">
                                            <b-form-input id="style-code" v-model="item.style_code" placeholder="Enter style code" required size="sm"></b-form-input>
                                        </b-form-group>
                                        <b-form-group label="Name" label-for="style-name">
                                            <b-form-input id="style-name" v-model="item.style_name" placeholder="Enter style name" required size="sm"></b-form-input>
                                        </b-form-group>
                                        <b-form-group label="Description" label-for="style-description">
                                            <b-form-textarea id="style-description" v-model="item.description" placeholder="Enter description" rows="2" size="sm"></b-form-textarea>
                                        </b-form-group>
                                    </template>
                                </attribute-management>
                            </b-tab>
                            <b-tab title="Styles">
                                <attribute-management ref="styleManager" title="Product Styles" :items="global_styles" :fields="[
                                    {key: 'style_code', label: 'Code', sortable: true},
                                    {key: 'style_name', label: 'Name', sortable: true},
                                    {key: 'description', label: 'Description'},
                                    {key: 'actions', label: ''}
                                ]" item-key="id" :fetch-method="fetch_global_styles" :save-method="save_style" @refresh="fetch_global_styles">
                                    <template v-slot:form-fields="{ item }">
                                        <b-form-group label="Code" label-for="style-code">
                                            <b-form-input id="style-code" v-model="item.style_code" placeholder="Enter style code" required size="sm"></b-form-input>
                                        </b-form-group>
                                        <b-form-group label="Name" label-for="style-name">
                                            <b-form-input id="style-name" v-model="item.style_name" placeholder="Enter style name" required size="sm"></b-form-input>
                                        </b-form-group>
                                        <b-form-group label="Description" label-for="style-description">
                                            <b-form-textarea id="style-description" v-model="item.description" placeholder="Enter description" rows="2" size="sm"></b-form-textarea>
                                        </b-form-group>
                                    </template>
                                </attribute-management>
                            </b-tab>
                            <!-- ADDED: Shapes Tab -->
                            <b-tab title="Shapes">
                                <attribute-management ref="sectionalShapeManager" title="Sectional Shapes" :items="global_sectional_shapes" :fields="[
                                    {key: 'shape_code', label: 'Code', sortable: true},
                                    {key: 'shape_name', label: 'Name', sortable: true},
                                    {key: 'description', label: 'Description'},
                                    {key: 'actions', label: ''}
                                ]" item-key="id" :fetch-method="fetch_global_sectional_shapes" :save-method="save_sectional_shape" @refresh="fetch_global_sectional_shapes">
                                    <template v-slot:form-fields="{ item }">
                                        <b-form-group label="Code" label-for="shape-code">
                                            <b-form-input id="shape-code" v-model="item.shape_code" placeholder="Enter shape code" required size="sm"></b-form-input>
                                        </b-form-group>
                                        <b-form-group label="Name" label-for="shape-name">
                                            <b-form-input id="shape-name" v-model="item.shape_name" placeholder="Enter shape name" required size="sm"></b-form-input>
                                        </b-form-group>
                                        <b-form-group label="Description" label-for="shape-description">
                                            <b-form-textarea id="shape-description" v-model="item.description" placeholder="Enter description" rows="2" size="sm"></b-form-textarea>
                                        </b-form-group>
                                    </template>
                                </attribute-management>
                            </b-tab>
                        </b-tabs>
                    </div>
                </b-tab>
            </b-tabs>
        </div>

        <!-- Manage Attribute Modal -->
        <b-modal v-model="showManageModal" :title="`Manage ${manageAttributeType}`" size="lg" hide-footer>
            <component :is="manageAttributeComponent" v-if="showManageModal" ref="attributeManager" @close="showManageModal = false" @saved="handleAttributeSaved"></component>
        </b-modal>
    </div>
</template>

<script>
    import Requestor from '@/utilities/Requestor'
    import Loading from 'vue-loading-overlay';
    import 'vue-loading-overlay/dist/vue-loading.css';
    import AttributeManagement from '@/components/ControlPanel/AttributeManagement.vue';

    export default {
        name: "ListCategorys",
        components: { 
            Loading,
            AttributeManagement
        },
        data: function() {
            return {
                packet: {},
                items: [],
                items_being_loaded: false,
                activeTab: 0,
                global_types: [],
                global_colors: [],
                global_sizes: [],
                global_materials: [],
                global_functionalities: [],
                global_bed_type_styles: [],
                global_styles: [],
                global_sectional_shapes: [],
                showManageModal: false,
                manageAttributeType: '',
                manageAttributeComponent: null
            }
        },
        methods: {
            formatDate(timestamp) {
                if (!timestamp) return 'Not saved';
                try {
                    if (typeof timestamp === 'string') {
                        const date = new Date(timestamp);
                        if (!isNaN(date.getTime())) {
                            return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                        }
                        return timestamp;
                    }
                    if (typeof timestamp === 'object' && timestamp.toDate) {
                        const date = timestamp.toDate();
                        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                    }
                    if (typeof timestamp === 'number') {
                        return new Date(timestamp).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                    }
                    return String(timestamp);
                } catch (e) {
                    return 'Invalid date';
                }
            },
            
            toggle_category_section(item, section) {
                if (section === 'subcategories') {
                    item.show_subcategories = !item.show_subcategories;
                    if (item.show_subcategories && item.category_id && (!item.subcategories || item.subcategories.length === 0)) {
                        this.fetch_subcategories(item);
                    }
                } else if (section === 'attributes') {
                    item.show_attributes = !item.show_attributes;
                    if (item.show_attributes && item.category_id) {
                        this.fetch_category_attributes(item);
                    }
                }
            },
            
            fetch_subcategories(item) {
                if (!item.category_id) return;
                
                let path = "/api/category/get_subcategories/";
                let data = { category_id: item.category_id };
                let that = this;
                
                Requestor.postSyncP(path, data, this,
                    function(response){
                        if (response.data.error){
                            that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant:"warning"});
                        } else {
                            item.subcategories = (response.data.result || []).map(subcat => {
                                return {
                                    ...subcat,
                                    properties_string: that.formatPropertiesForDisplay(subcat.properties),
                                    show_subsubcategories: false,
                                    subsubcategories: []
                                };
                            });
                        }
                    },
                    function(error){
                        console.error("Error loading subcategories:", error);
                    }
                );
            },
            
            formatPropertiesForDisplay(properties) {
                if (!properties) return '';
                if (typeof properties === 'string') {
                    try {
                        const parsed = JSON.parse(properties);
                        return JSON.stringify(parsed);
                    } catch (e) {
                        return properties;
                    }
                }
                if (typeof properties === 'object') {
                    return JSON.stringify(properties);
                }
                return String(properties);
            },
            
            fetch_category_attributes(item) {
                if (!item.category_id) return;
                
                this.fetch_types_by_category(item);
                this.fetch_colors_by_category(item);
                this.fetch_sizes_by_category(item);
                this.fetch_materials_by_category(item);
                this.fetch_functionalities_by_category(item);
                this.fetch_bed_type_styles_by_category(item);
                this.fetch_styles_by_category(item);
                this.fetch_sectional_shapes_by_category(item);
            },
            
            fetch_types_by_category(item) {
                let path = "/api/category/get_types_by_category/";
                let data = { category_id: item.category_id };
                
                Requestor.postSyncP(path, data, this,
                    function(response){
                        if (!response.data.error) {
                            item.types = response.data.result || [];
                        }
                    },
                    function(error){
                        console.error("Error loading types:", error);
                        item.types = [];
                    }
                );
            },
            
            fetch_colors_by_category(item) {
                let path = "/api/category/get_colors_by_category/";
                let data = { category_id: item.category_id };
                
                Requestor.postSyncP(path, data, this,
                    function(response){
                        if (!response.data.error) {
                            item.colors = response.data.result || [];
                        }
                    },
                    function(error){
                        console.error("Error loading colors:", error);
                        item.colors = [];
                    }
                );
            },
            
            fetch_sizes_by_category(item) {
                let path = "/api/category/get_sizes_by_category/";
                let data = { category_id: item.category_id };
                
                Requestor.postSyncP(path, data, this,
                    function(response){
                        if (!response.data.error) {
                            item.sizes = response.data.result || [];
                        }
                    },
                    function(error){
                        console.error("Error loading sizes:", error);
                        item.sizes = [];
                    }
                );
            },
            
            fetch_materials_by_category(item) {
                let path = "/api/category/get_materials_by_category/";
                let data = { category_id: item.category_id };
                
                Requestor.postSyncP(path, data, this,
                    function(response){
                        if (!response.data.error) {
                            item.materials = response.data.result || [];
                        }
                    },
                    function(error){
                        console.error("Error loading materials:", error);
                        item.materials = [];
                    }
                );
            },
            
            fetch_functionalities_by_category(item) {
                let path = "/api/category/get_functionalities_by_category/";
                let data = { category_id: item.category_id };
                
                Requestor.postSyncP(path, data, this,
                    function(response){
                        if (!response.data.error) {
                            item.functionalities = response.data.result || [];
                        }
                    },
                    function(error){
                        console.error("Error loading functionalities:", error);
                        item.functionalities = [];
                    }
                );
            },
            
            fetch_bed_type_styles_by_category(item) {
                let path = "/api/category/get_bed_type_styles_by_category/";
                let data = { category_id: item.category_id };
                
                Requestor.postSyncP(path, data, this,
                    function(response){
                        if (!response.data.error) {
                            item.bed_type_styles = response.data.result || [];
                        }
                    },
                    function(error){
                        console.error("Error loading bed types:", error);
                        item.bed_type_styles = [];
                    }
                );
            },
            
            fetch_styles_by_category(item) {
                let path = "/api/category/get_styles_by_category/";
                let data = { category_id: item.category_id };
                
                Requestor.postSyncP(path, data, this,
                    function(response){
                        if (!response.data.error) {
                            item.styles = response.data.result || [];
                        }
                    },
                    function(error){
                        console.error("Error loading styles:", error);
                        item.styles = [];
                    }
                );
            },
            
            fetch_sectional_shapes_by_category(item) {
                let path = "/api/category/get_sectional_shapes_by_category/";
                let data = { category_id: item.category_id };
                
                Requestor.postSyncP(path, data, this,
                    function(response){
                        if (!response.data.error) {
                            item.sectional_shapes = response.data.result || [];
                        }
                    },
                    function(error){
                        console.error("Error loading sectional shapes:", error);
                        item.sectional_shapes = [];
                    }
                );
            },
            
            save_category_types(item) {
                if (!item.category_id) {
                    this.$bvToast.toast("Please save the category first.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    return;
                }
                
                let type_ids = (item.types || []).filter(type => type.is_mapped).map(type => type.id);
                let path = "/api/category/update_category_type_mapping/";
                let data = { category_id: item.category_id, type_ids: type_ids };
                this.makeRequest(path, data, "Type mappings saved.");
            },
            
            save_category_colors(item) {
                if (!item.category_id) {
                    this.$bvToast.toast("Please save the category first.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    return;
                }
                
                let color_ids = (item.colors || []).filter(color => color.is_mapped).map(color => color.id);
                let path = "/api/category/update_category_color_mapping/";
                let data = { category_id: item.category_id, color_ids: color_ids };
                this.makeRequest(path, data, "Color mappings saved.");
            },
            
            save_category_sizes(item) {
                if (!item.category_id) {
                    this.$bvToast.toast("Please save the category first.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    return;
                }
                
                let size_ids = (item.sizes || []).filter(size => size.is_mapped).map(size => size.id);
                let path = "/api/category/update_category_size_mapping/";
                let data = { category_id: item.category_id, size_ids: size_ids };
                this.makeRequest(path, data, "Size mappings saved.");
            },
            
            save_category_materials(item) {
                if (!item.category_id) {
                    this.$bvToast.toast("Please save the category first.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    return;
                }
                
                let material_ids = (item.materials || []).filter(material => material.is_mapped).map(material => material.id);
                let path = "/api/category/update_category_material_mapping/";
                let data = { category_id: item.category_id, material_ids: material_ids };
                this.makeRequest(path, data, "Material mappings saved.");
            },
            
            save_category_functionalities(item) {
                if (!item.category_id) {
                    this.$bvToast.toast("Please save the category first.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    return;
                }
                
                let functionality_ids = (item.functionalities || []).filter(func => func.is_mapped).map(func => func.id);
                let path = "/api/category/update_category_functionality_mapping/";
                let data = { category_id: item.category_id, functionality_ids: functionality_ids };
                this.makeRequest(path, data, "Functionality mappings saved.");
            },
            
            save_category_bed_type_styles(item) {
                if (!item.category_id) {
                    this.$bvToast.toast("Please save the category first.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    return;
                }
                
                let style_ids = (item.bed_type_styles || []).filter(style => style.is_mapped).map(style => style.id);
                let path = "/api/category/update_category_bed_type_style_mapping/";
                let data = { category_id: item.category_id, bed_type_style_ids: style_ids };
                this.makeRequest(path, data, "Bed type mappings saved.");
            },
            
            save_category_styles(item) {
                if (!item.category_id) {
                    this.$bvToast.toast("Please save the category first.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    return;
                }
                
                let style_ids = (item.styles || []).filter(style => style.is_mapped).map(style => style.id);
                let path = "/api/category/update_category_style_mapping/";
                let data = { category_id: item.category_id, style_ids: style_ids };
                this.makeRequest(path, data, "Style mappings saved.");
            },
            
            save_category_sectional_shapes(item) {
                if (!item.category_id) {
                    this.$bvToast.toast("Please save the category first.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    return;
                }
                
                let shape_ids = (item.sectional_shapes || []).filter(shape => shape.is_mapped).map(shape => shape.id);
                let path = "/api/category/update_category_sectional_shape_mapping/";
                let data = { category_id: item.category_id, sectional_shape_ids: shape_ids };
                this.makeRequest(path, data, "Sectional shape mappings saved.");
            },
            
            makeRequest(path, data, successMessage) {
                let that = this;
                Requestor.postSyncP(path, data, this,
                    function(response){
                        if (response.data.error){
                            that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant:"warning"});
                        } else {
                            that.$bvToast.toast(successMessage, {title: "Success", autoHideDelay: 1500, variant:"success"});
                        }
                    },
                    function(error){
                        console.error("Request error:", error);
                        that.$bvToast.toast("Error saving. Please try again.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    }
                );
            },
            
            // Subcategory Methods
            toggle_subsubcategories(item, subcategory) {
                subcategory.show_subsubcategories = !subcategory.show_subsubcategories;
                if (subcategory.show_subsubcategories && subcategory.id && (!subcategory.subsubcategories || subcategory.subsubcategories.length === 0)) {
                    this.fetch_subsubcategories(subcategory);
                }
            },
            
            fetch_subsubcategories(subcategory) {
                if (!subcategory.id) return;
                
                let path = "/api/category/get_subsubcategories/";
                let data = { subcategory_id: subcategory.id };
                let that = this;
                
                Requestor.postSyncP(path, data, this,
                    function(response){
                        if (response.data.error){
                            that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant:"warning"});
                        } else {
                            subcategory.subsubcategories = (response.data.result || []).map(subsubcat => {
                                return {
                                    ...subsubcat,
                                    properties_string: that.formatPropertiesForDisplay(subsubcat.properties)
                                };
                            });
                        }
                    },
                    function(error){
                        console.error("Error loading sub-subcategories:", error);
                    }
                );
            },
            
            add_new_subsubcategory(subcategory) {
                if (!subcategory.id) {
                    this.$bvToast.toast("Please save the subcategory first.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    return;
                }
                
                if (!subcategory.subsubcategories) {
                    subcategory.subsubcategories = [];
                }
                
                let newSubsubcategory = {
                    id: null,
                    subcategory_id: subcategory.id,
                    subsubcategory_name: '',
                    subsubcategory_code: '',
                    subsubcategory_description: '',
                    properties: {},
                    properties_string: ''
                };
                subcategory.subsubcategories.unshift(newSubsubcategory);
            },
            
            save_subsubcategory(subcategory, subsubcategory) {
                if (!subcategory.id) {
                    this.$bvToast.toast("Subcategory must be saved first.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    return;
                }
                
                if (!subsubcategory.subsubcategory_name || subsubcategory.subsubcategory_name.trim() === '') {
                    this.$bvToast.toast("Sub-subcategory name is required.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    return;
                }
                
                if (!subsubcategory.subsubcategory_code || subsubcategory.subsubcategory_code.trim() === '') {
                    this.$bvToast.toast("Sub-subcategory code is required.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    return;
                }
                
                let properties = {};
                if (subsubcategory.properties_string && subsubcategory.properties_string.trim() !== '') {
                    try {
                        properties = JSON.parse(subsubcategory.properties_string);
                    } catch (e) {
                        this.$bvToast.toast("Invalid JSON in properties field.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                        return;
                    }
                }
                
                let path = "/api/category/save_subsubcategory/";
                let data = {
                    id: subsubcategory.id || null,
                    subcategory_id: subcategory.id,
                    subsubcategory_name: subsubcategory.subsubcategory_name,
                    subsubcategory_code: subsubcategory.subsubcategory_code,
                    subsubcategory_description: subsubcategory.subsubcategory_description || '',
                    properties: properties
                };
                
                let that = this;
                Requestor.postSyncP(path, data, this,
                    function(response){
                        if (response.data.error){
                            that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant:"warning"});
                        } else {
                            that.$bvToast.toast("Sub-subcategory saved.", {title: "Success", autoHideDelay: 1500, variant:"success"});
                            if (response.data.result && response.data.result[0]) {
                                subsubcategory.id = response.data.result[0];
                            }
                            that.fetch_subsubcategories(subcategory);
                        }
                    },
                    function(error){
                        console.error("Error saving sub-subcategory:", error);
                        that.$bvToast.toast("Error saving sub-subcategory.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    }
                );
            },
            
            delete_subsubcategory(subcategory, subsubcategory, index) {
                if (!subsubcategory.id) {
                    subcategory.subsubcategories.splice(index, 1);
                    return;
                }
                
                if (!confirm("Delete this sub-subcategory?")) return;
                
                let path = "/api/category/delete_subsubcategory/";
                let data = { id: subsubcategory.id };
                let that = this;
                
                Requestor.postSyncP(path, data, this,
                    function(response){
                        if (response.data.error){
                            that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant:"warning"});
                        } else {
                            that.$bvToast.toast("Sub-subcategory deleted.", {title: "Success", autoHideDelay: 1500, variant:"success"});
                            subcategory.subsubcategories.splice(index, 1);
                        }
                    },
                    function(error){
                        console.error("Error deleting sub-subcategory:", error);
                        that.$bvToast.toast("Error deleting sub-subcategory.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    }
                );
            },
            
            save_subcategory(item, subcategory) {
                if (!item.category_id) {
                    this.$bvToast.toast("Category must be saved first.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    return;
                }
                
                if (!subcategory.subcategory_name || subcategory.subcategory_name.trim() === '') {
                    this.$bvToast.toast("Subcategory name is required.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    return;
                }
                
                if (!subcategory.subcategory_code || subcategory.subcategory_code.trim() === '') {
                    this.$bvToast.toast("Subcategory code is required.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    return;
                }
                
                let properties = {};
                if (subcategory.properties_string && subcategory.properties_string.trim() !== '') {
                    try {
                        properties = JSON.parse(subcategory.properties_string);
                    } catch (e) {
                        this.$bvToast.toast("Invalid JSON in properties field.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                        return;
                    }
                }
                
                let path = "/api/category/save_subcategory/";
                let data = {
                    id: subcategory.id || null,
                    category_id: item.category_id,
                    subcategory_name: subcategory.subcategory_name,
                    subcategory_code: subcategory.subcategory_code,
                    subcategory_description: subcategory.subcategory_description || '',
                    properties: properties
                };
                
                let that = this;
                Requestor.postSyncP(path, data, this,
                    function(response){
                        if (response.data.error){
                            that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant:"warning"});
                        } else {
                            that.$bvToast.toast("Subcategory saved.", {title: "Success", autoHideDelay: 1500, variant:"success"});
                            if (response.data.result && response.data.result[0]) {
                                subcategory.id = response.data.result[0];
                            }
                            that.fetch_subcategories(item);
                        }
                    },
                    function(error){
                        console.error("Error saving subcategory:", error);
                        that.$bvToast.toast("Error saving subcategory.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    }
                );
            },
            
            delete_subcategory(item, subcategory, index) {
                if (!subcategory.id) {
                    item.subcategories.splice(index, 1);
                    return;
                }
                
                if (!confirm("Delete this subcategory?")) return;
                
                let path = "/api/category/delete_subcategory/";
                let data = { id: subcategory.id };
                let that = this;
                
                Requestor.postSyncP(path, data, this,
                    function(response){
                        if (response.data.error){
                            that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant:"warning"});
                        } else {
                            that.$bvToast.toast("Subcategory deleted.", {title: "Success", autoHideDelay: 1500, variant:"success"});
                            item.subcategories.splice(index, 1);
                        }
                    },
                    function(error){
                        console.error("Error deleting subcategory:", error);
                        that.$bvToast.toast("Error deleting subcategory.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    }
                );
            },
            
            add_new_subcategory(item) {
                if (!item.category_id) {
                    this.$bvToast.toast("Please save the category first.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    return;
                }
                
                if (!item.subcategories) {
                    item.subcategories = [];
                }
                
                let newSubcategory = {
                    id: null,
                    category_id: item.category_id,
                    subcategory_name: '',
                    subcategory_code: '',
                    subcategory_description: '',
                    properties: {},
                    properties_string: '',
                    show_subsubcategories: false,
                    subsubcategories: []
                };
                item.subcategories.unshift(newSubcategory);
            },
            
            remove_category(idx) {
                const item = this.items[idx];
                if (item.category_id) {
                    if (!confirm("Remove this category from the list? (It won't be deleted from database)")) {
                        return;
                    }
                }
                this.items.splice(idx, 1);
            },
            
            save_category(item) {
                if (!item.category_name || item.category_name.trim() === '') {
                    this.$bvToast.toast("Category name is required.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    return;
                }
                
                if (!item.category_code || item.category_code.trim() === '') {
                    this.$bvToast.toast("Category code is required.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    return;
                }
                
                let path = "/api/category/save_category/";
                let data = {
                    category_id: item.category_id || null,
                    category_name: item.category_name,
                    category_code: item.category_code,
                    category_description: item.category_description || ''
                };
                
                let that = this;
                this.items_being_loaded = true;
                
                Requestor.postSyncP(path, data, this,
                    function(response){
                        that.items_being_loaded = false;
                        
                        if (response.data.error){
                            that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant:"warning"});
                        } else {
                            that.$bvToast.toast("Category saved.", {title: "Success", autoHideDelay: 1500, variant:"success"});
                            
                            if (response.data.result && response.data.result[0]) {
                                item.category_id = response.data.result[0];
                                item.ts = new Date().toISOString();
                            }
                            
                            that.fetch_init();
                        }
                    },
                    function(error){
                        console.error("Error saving category:", error);
                        that.items_being_loaded = false;
                        that.$bvToast.toast("Error saving category.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    }
                );
            },
            
            fetch_init() {
                let path = "/api/category/get_all_categories/";
                let that = this;
                
                this.items_being_loaded = true;
                
                Requestor.getSyncP(path, {}, this,
                    function(response){
                        that.items_being_loaded = false;
                        
                        if (response.data.error){
                            that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant:"warning"});
                            that.items = [];
                        } else {
                            that.items = (response.data.result || []).map(item => {
                                return {
                                    ...item,
                                    category_name: item.category_name || '',
                                    category_code: item.category_code || '',
                                    category_description: item.category_description || '',
                                    ts: item.ts || '',
                                    show_subcategories: false,
                                    show_attributes: false,
                                    subcategories: [],
                                    types: [],
                                    colors: [],
                                    sizes: [],
                                    materials: [],
                                    functionalities: [],
                                    bed_type_styles: [],
                                    styles: [],
                                    sectional_shapes: []
                                };
                            });
                        }
                    },
                    function(error){
                        console.error("Error loading categories:", error);
                        that.items_being_loaded = false;
                        that.$bvToast.toast("Error loading categories.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                        that.items = [];
                    }
                );
            },
            
            add_new_category() {
                let item = {
                    category_id: null,
                    category_name: '',
                    category_code: '',
                    category_description: '',
                    ts: '',
                    show_subcategories: false,
                    show_attributes: false,
                    subcategories: [],
                    types: [],
                    colors: [],
                    sizes: [],
                    materials: [],
                    functionalities: [],
                    bed_type_styles: [],
                    styles: [],
                    sectional_shapes: []
                };
                this.items.unshift(item);
            },
            
            show_manage_modal(attributeType) {
                this.manageAttributeType = attributeType.charAt(0).toUpperCase() + attributeType.slice(1) + 's';
                this.manageAttributeComponent = AttributeManagement;
                this.showManageModal = true;
            },
            
            fetch_global_types() {
                let path = "/api/category/get_all_types/";
                let that = this;
                
                Requestor.getSyncP(path, {}, this,
                    function(response){
                        if (response.data.error){
                            that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant:"warning"});
                        } else {
                            that.global_types = response.data.result || [];
                        }
                    },
                    function(error){
                        console.error("Error loading types:", error);
                    }
                );
            },
            
            fetch_global_colors() {
                let path = "/api/category/get_all_colors/";
                let that = this;
                
                Requestor.getSyncP(path, {}, this,
                    function(response){
                        if (response.data.error){
                            that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant:"warning"});
                        } else {
                            that.global_colors = response.data.result || [];
                        }
                    },
                    function(error){
                        console.error("Error loading colors:", error);
                    }
                );
            },
            
            fetch_global_sizes() {
                let path = "/api/category/get_all_sizes/";
                let that = this;
                
                Requestor.getSyncP(path, {}, this,
                    function(response){
                        if (response.data.error){
                            that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant:"warning"});
                        } else {
                            that.global_sizes = response.data.result || [];
                        }
                    },
                    function(error){
                        console.error("Error loading sizes:", error);
                    }
                );
            },
            
            fetch_global_materials() {
                let path = "/api/category/get_all_materials/";
                let that = this;
                
                Requestor.getSyncP(path, {}, this,
                    function(response){
                        if (response.data.error){
                            that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant:"warning"});
                        } else {
                            that.global_materials = response.data.result || [];
                        }
                    },
                    function(error){
                        console.error("Error loading materials:", error);
                    }
                );
            },
            
            fetch_global_functionalities() {
                let path = "/api/category/get_all_functionalities/";
                let that = this;
                
                Requestor.getSyncP(path, {}, this,
                    function(response){
                        if (response.data.error){
                            that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant:"warning"});
                        } else {
                            that.global_functionalities = response.data.result || [];
                        }
                    },
                    function(error){
                        console.error("Error loading functionalities:", error);
                    }
                );
            },
            
            fetch_global_bed_type_styles() {
                let path = "/api/category/get_all_bed_type_styles/";
                let that = this;
                
                Requestor.getSyncP(path, {}, this,
                    function(response){
                        if (response.data.error){
                            that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant:"warning"});
                        } else {
                            that.global_bed_type_styles = response.data.result || [];
                        }
                    },
                    function(error){
                        console.error("Error loading bed types:", error);
                    }
                );
            },
            
            fetch_global_styles() {
                let path = "/api/category/get_all_styles/";
                let that = this;
                
                Requestor.getSyncP(path, {}, this,
                    function(response){
                        if (response.data.error){
                            that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant:"warning"});
                        } else {
                            that.global_styles = response.data.result || [];
                        }
                    },
                    function(error){
                        console.error("Error loading styles:", error);
                    }
                );
            },
            
            fetch_global_sectional_shapes() {
                let path = "/api/category/get_all_sectional_shapes/";
                let that = this;
                
                Requestor.getSyncP(path, {}, this,
                    function(response){
                        if (response.data.error){
                            that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant:"warning"});
                        } else {
                            that.global_sectional_shapes = response.data.result || [];
                        }
                    },
                    function(error){
                        console.error("Error loading sectional shapes:", error);
                    }
                );
            },
            
            save_type(item) {
                let path = "/api/category/save_type/";
                let that = this;
                
                Requestor.postSyncP(path, item, this,
                    function(response){
                        if (response.data.error){
                            that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant:"warning"});
                        } else {
                            that.$bvToast.toast("Type saved.", {title: "Success", autoHideDelay: 1500, variant:"success"});
                            that.fetch_global_types();
                            that.refreshCategoryAttributes();
                        }
                    },
                    function(error){
                        console.error("Error saving type:", error);
                    }
                );
            },
            
            save_color(item) {
                let path = "/api/category/save_color/";
                let that = this;
                
                Requestor.postSyncP(path, item, this,
                    function(response){
                        if (response.data.error){
                            that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant:"warning"});
                        } else {
                            that.$bvToast.toast("Color saved.", {title: "Success", autoHideDelay: 1500, variant:"success"});
                            that.fetch_global_colors();
                            that.refreshCategoryAttributes();
                        }
                    },
                    function(error){
                        console.error("Error saving color:", error);
                    }
                );
            },
            
            save_size(item) {
                let path = "/api/category/save_size/";
                let that = this;
                
                Requestor.postSyncP(path, item, this,
                    function(response){
                        if (response.data.error){
                            that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant:"warning"});
                        } else {
                            that.$bvToast.toast("Size saved.", {title: "Success", autoHideDelay: 1500, variant:"success"});
                            that.fetch_global_sizes();
                            that.refreshCategoryAttributes();
                        }
                    },
                    function(error){
                        console.error("Error saving size:", error);
                    }
                );
            },
            
            save_material(item) {
                let path = "/api/category/save_material/";
                let that = this;
                
                Requestor.postSyncP(path, item, this,
                    function(response){
                        if (response.data.error){
                            that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant:"warning"});
                        } else {
                            that.$bvToast.toast("Material saved.", {title: "Success", autoHideDelay: 1500, variant:"success"});
                            that.fetch_global_materials();
                            that.refreshCategoryAttributes();
                        }
                    },
                    function(error){
                        console.error("Error saving material:", error);
                    }
                );
            },
            
            save_functionality(item) {
                let path = "/api/category/save_functionality/";
                let that = this;
                
                Requestor.postSyncP(path, item, this,
                    function(response){
                        if (response.data.error){
                            that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant:"warning"});
                        } else {
                            that.$bvToast.toast("Functionality saved.", {title: "Success", autoHideDelay: 1500, variant:"success"});
                            that.fetch_global_functionalities();
                            that.refreshCategoryAttributes();
                        }
                    },
                    function(error){
                        console.error("Error saving functionality:", error);
                    }
                );
            },
            
            save_bed_type_style(item) {
                let path = "/api/category/save_bed_type_style/";
                let that = this;
                
                Requestor.postSyncP(path, item, this,
                    function(response){
                        if (response.data.error){
                            that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant:"warning"});
                        } else {
                            that.$bvToast.toast("Bed type saved.", {title: "Success", autoHideDelay: 1500, variant:"success"});
                            that.fetch_global_bed_type_styles();
                            that.refreshCategoryAttributes();
                        }
                    },
                    function(error){
                        console.error("Error saving bed type:", error);
                    }
                );
            },
            
            save_style(item) {
                let path = "/api/category/save_style/";
                let that = this;
                
                Requestor.postSyncP(path, item, this,
                    function(response){
                        if (response.data.error){
                            that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant:"warning"});
                        } else {
                            that.$bvToast.toast("Style saved.", {title: "Success", autoHideDelay: 1500, variant:"success"});
                            that.fetch_global_styles();
                            that.refreshCategoryAttributes();
                        }
                    },
                    function(error){
                        console.error("Error saving style:", error);
                    }
                );
            },
            
            save_sectional_shape(item) {
                let path = "/api/category/save_sectional_shape/";
                let that = this;
                
                Requestor.postSyncP(path, item, this,
                    function(response){
                        if (response.data.error){
                            that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant:"warning"});
                        } else {
                            that.$bvToast.toast("Sectional shape saved.", {title: "Success", autoHideDelay: 1500, variant:"success"});
                            that.fetch_global_sectional_shapes();
                            that.refreshCategoryAttributes();
                        }
                    },
                    function(error){
                        console.error("Error saving sectional shape:", error);
                    }
                );
            },
            
            refreshCategoryAttributes() {
                this.items.forEach(cat => {
                    if (cat.show_attributes && cat.category_id) {
                        this.fetch_category_attributes(cat);
                    }
                });
            },
            
            handleAttributeSaved() {
                this.showManageModal = false;
                this.fetch_global_types();
                this.fetch_global_colors();
                this.fetch_global_sizes();
                this.fetch_global_materials();
                this.fetch_global_functionalities();
                this.fetch_global_bed_type_styles();
                this.fetch_global_styles();
                this.fetch_global_sectional_shapes();
                this.refreshCategoryAttributes();
            }
        },
        created() {
            try {
                this.packet = JSON.parse(localStorage.packet || '{}');
                this.packet.userDetails = JSON.parse(localStorage.userDetails || '{}');
            } catch (error) {
                console.error("Error loading user data:", error);
                this.packet = { userDetails: {} };
            }
            
            this.fetch_init();
            this.fetch_global_types();
            this.fetch_global_colors();
            this.fetch_global_sizes();
            this.fetch_global_materials();
            this.fetch_global_functionalities();
            this.fetch_global_bed_type_styles();
            this.fetch_global_styles();
            this.fetch_global_sectional_shapes();
        }
    }
</script>

<style scoped>
.inventory-container {
    min-height: 100vh;
    padding: 20px;
    background: #f5f7fa;
}

.categories-management-container {
    background: white;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    padding: 20px;
    max-width: 1400px;
    margin: 0 auto;
}

/* Header */
.header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 1px solid #e9ecef;
}

.page-title {
    font-size: 1.5rem;
    font-weight: 600;
    margin: 0;
    color: #1a1a2e;
}

.page-subtitle {
    font-size: 0.85rem;
    color: #6c757d;
    margin: 4px 0 0;
}

.action-buttons {
    display: flex;
    gap: 8px;
}

.action-btn {
    padding: 6px 14px;
    font-size: 0.85rem;
}

/* Compact Alert */
.compact-alert {
    padding: 8px 12px;
    margin-bottom: 15px;
    font-size: 0.8rem;
}

/* Category Row */
.category-row {
    border: 1px solid #e9ecef;
    border-radius: 6px;
    margin-bottom: 12px;
    background: white;
}

.category-main-row {
    padding: 12px;
}

.category-fields {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: flex-end;
    margin-bottom: 10px;
}

.field-group {
    flex: 0 0 auto;
    min-width: 100px;
}

.field-group-expanded {
    flex: 1;
    min-width: 150px;
}

.category-id-group {
    min-width: 70px;
}

.date-group {
    min-width: 110px;
}

.action-group {
    min-width: 80px;
}

.field-label {
    display: block;
    font-size: 0.65rem;
    font-weight: 600;
    color: #6c757d;
    text-transform: uppercase;
    margin-bottom: 2px;
}

.field-input {
    font-size: 0.8rem;
    padding: 4px 8px;
    height: 30px;
}

.save-cat-btn {
    padding: 4px 10px;
    font-size: 0.75rem;
}

.category-actions-row {
    display: flex;
    gap: 8px;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #f0f0f0;
}

.toggle-btn {
    padding: 4px 12px;
    font-size: 0.75rem;
}

/* Subcategories Section */
.subcategories-section {
    border-top: 1px solid #e9ecef;
    background: #f8f9fa;
    padding: 12px;
}

.subcategories-wrapper {
    background: white;
    border-radius: 4px;
    padding: 12px;
}

.subcategories-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.section-title {
    font-size: 0.85rem;
    font-weight: 600;
    margin: 0;
}

.subcategories-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.subcategory-row {
    border: 1px solid #e9ecef;
    border-radius: 4px;
    background: white;
    padding: 8px;
}

.subcategory-fields {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
}

.small-group {
    min-width: 30px;
}

.index-badge {
    display: inline-block;
    padding: 2px 6px;
    background: #e9ecef;
    border-radius: 3px;
    font-size: 0.7rem;
    font-weight: 600;
}

.field-label-sm {
    font-size: 0.6rem;
    font-weight: 600;
    color: #6c757d;
    display: block;
    margin-bottom: 2px;
}

.field-input-sm {
    font-size: 0.75rem;
    padding: 3px 6px;
    height: 26px;
}

.action-group-sm {
    display: flex;
    gap: 4px;
}

.action-icon-btn {
    padding: 2px 6px;
    font-size: 0.7rem;
}

.toggle-sub-btn {
    padding: 2px 8px;
    font-size: 0.7rem;
}

/* Sub-Subcategories */
.subsubcategories-section {
    margin-top: 10px;
    padding-left: 20px;
}

.subsubcategories-wrapper {
    background: #f8f9fa;
    border-radius: 4px;
    padding: 10px;
}

.subsubcategories-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.subsubcategories-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.subsubcategory-row {
    border: 1px solid #e9ecef;
    border-radius: 3px;
    background: white;
    padding: 6px;
}

.subsubcategory-fields {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    align-items: center;
}

.tiny-group {
    min-width: 25px;
}

.tiny-badge {
    display: inline-block;
    padding: 2px 4px;
    background: #e9ecef;
    border-radius: 2px;
    font-size: 0.65rem;
}

.field-input-xs {
    font-size: 0.7rem;
    padding: 2px 5px;
    height: 24px;
}

.action-group-xs {
    display: flex;
    gap: 3px;
}

.action-icon-btn-xs {
    padding: 1px 4px;
    font-size: 0.65rem;
}

.empty-subsub, .empty-subcategories {
    text-align: center;
    padding: 10px;
}

/* Attributes Section */
.attributes-section {
    border-top: 1px solid #e9ecef;
    background: #f8f9fa;
    padding: 12px;
}

.attributes-wrapper {
    background: white;
    border-radius: 4px;
    padding: 12px;
}

.attribute-content {
    padding: 8px 0;
}

.attribute-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e9ecef;
}

.checkbox-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 8px;
}

.color-dot {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 2px;
    margin-right: 6px;
    vertical-align: middle;
}

/* Global Attributes */
.global-attributes-container {
    padding: 15px;
    background: #f8f9fa;
    border-radius: 6px;
}

/* Tabs */
.inventory-tabs :deep(.nav-tabs) {
    border-bottom: 1px solid #e9ecef;
}

.inventory-tabs :deep(.nav-link) {
    padding: 8px 16px;
    font-size: 0.85rem;
    color: #6c757d;
}

.inventory-tabs :deep(.nav-link.active) {
    color: #007bff;
    font-weight: 500;
}

.attributes-tabs :deep(.nav-link) {
    padding: 6px 12px;
    font-size: 0.75rem;
}

.global-tabs :deep(.nav-link) {
    padding: 6px 12px;
    font-size: 0.8rem;
}

/* Loading */
.loading-container {
    padding: 40px;
    text-align: center;
}

/* Responsive */
@media (max-width: 1024px) {
    .category-fields {
        flex-direction: column;
        align-items: stretch;
    }
    
    .field-group, .field-group-expanded {
        width: 100%;
    }
    
    .action-group {
        margin-top: 8px;
    }
    
    .subcategory-fields {
        flex-direction: column;
        align-items: stretch;
    }
    
    .subsubcategory-fields {
        flex-direction: column;
        align-items: stretch;
    }
    
    .checkbox-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 768px) {
    .header-section {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }
    
    .action-buttons {
        width: 100%;
    }
    
    .action-btn {
        flex: 1;
    }
}
</style>


// WEBPACK FOOTER //
// src/components/ControlPanel/ListCategories.vue